import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sun, Plus, Pencil, Trash2, X } from "lucide-react";
import { BoardView } from "@/components/myday/BoardView";
import { TodoEditor } from "@/components/myday/TodoEditor";
import { QuickCapture } from "@/components/myday/QuickCapture";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const SUMMARY = [
  { key: "overdue", label: "Overdue", tone: "text-red-600" },
  { key: "due_today", label: "Due today", tone: "text-amber-600" },
  { key: "due_soon", label: "Due soon", tone: "text-sky-600" },
  { key: "open", label: "Open", tone: "text-slate-900" },
];

const DUE_STATE_FOR = { overdue: "overdue", due_today: "today", due_soon: "soon" };
const itemMatches = (i, key) => !i.completed && (key === "open" ? true : i.due_state === DUE_STATE_FOR[key]);
const filterBoards = (boards, key) => boards
  .map((b) => ({ ...b, checklists: b.checklists.map((c) => ({ ...c, items: c.items.filter((i) => itemMatches(i, key)) })).filter((c) => c.items.length) }))
  .filter((b) => b.checklists.length);

export default function MyDayPage() {
  const [data, setData] = useState(null);
  const [boardId, setBoardId] = useState(null);
  const [newBoard, setNewBoard] = useState("");
  const [editing, setEditing] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [boardTitle, setBoardTitle] = useState("");
  const [delChecklist, setDelChecklist] = useState(null);
  const [filter, setFilter] = useState(null);
  const boardRef = useRef(null);

  const load = useCallback(() => api.get("/todo").then((r) => { setData(r.data); setBoardId((b) => b && r.data.boards.some((x) => x.board_id === b) ? b : r.data.boards[0]?.board_id || null); }), []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const board = data?.boards.find((b) => b.board_id === boardId);

  const actions = {
    addChecklist: (bid, title) => run(() => api.post("/todo/checklists", { board_id: bid, title })),
    renameChecklist: (cid, title) => run(() => api.patch(`/todo/checklists/${cid}`, { title })),
    deleteChecklist: (c) => setDelChecklist(c),
    addNote: (cid, text) => run(() => api.post(`/todo/checklists/${cid}/notes`, { text }), "Note added"),
    editNote: (cid, nid, text) => run(() => api.patch(`/todo/checklists/${cid}/notes/${nid}`, { text }), "Note updated"),
    deleteNote: (cid, nid) => run(() => api.delete(`/todo/checklists/${cid}/notes/${nid}`), "Note deleted"),
    addItem: (cid, title) => run(() => api.post("/todo/items", { checklist_id: cid, title })),
    toggleItem: (item, completed) => {
      setData((d) => ({ ...d, boards: d.boards.map((b) => ({ ...b, checklists: b.checklists.map((c) => ({ ...c, items: c.items.map((i) => i.todo_id === item.todo_id ? { ...i, completed, due_state: completed ? null : i.due_state } : i) })) })) }));
      run(() => api.patch(`/todo/items/${item.todo_id}`, { completed }));
    },
    editItem: (item) => setEditing(item),
    reorderChecklists: (bid, ids) => {
      setData((d) => ({ ...d, boards: d.boards.map((b) => b.board_id !== bid ? b : { ...b, checklists: ids.map((id) => b.checklists.find((c) => c.checklist_id === id)) }) }));
      run(() => api.post("/todo/reorder", { kind: "checklists", parent_id: bid, ids }));
    },
    reorderItems: (cid, ids, fromCid) => {
      setData((d) => ({ ...d, boards: d.boards.map((b) => ({ ...b, checklists: b.checklists.map((c) => {
        const all = b.checklists.flatMap((x) => x.items);
        if (c.checklist_id === cid) return { ...c, items: [...ids.map((id) => ({ ...all.find((i) => i.todo_id === id), checklist_id: cid })), ...c.items.filter((i) => !ids.includes(i.todo_id) && i.completed !== all.find((x) => x.todo_id === ids[0])?.completed)] };
        if (c.checklist_id === fromCid) return { ...c, items: c.items.filter((i) => !ids.includes(i.todo_id)) };
        return c;
      }) })) }));
      run(() => api.post("/todo/reorder", { kind: "items", parent_id: cid, ids }));
    },
  };

  const addBoard = (e) => { e.preventDefault(); if (!newBoard.trim()) return; run(async () => { const { data: b } = await api.post("/todo/boards", { title: newBoard.trim() }); setBoardId(b.board_id); setNewBoard(""); }, "Board created"); };
  const saveBoardTitle = () => { setRenaming(false); if (boardTitle.trim() && boardTitle.trim() !== board.title) run(() => api.patch(`/todo/boards/${board.board_id}`, { title: boardTitle.trim() })); };
  const deleteBoard = () => { if (window.confirm(`Delete board "${board.title}" with all its checklists and to-dos?`)) run(() => api.delete(`/todo/boards/${board.board_id}`), "Board deleted"); };

  if (!data) return <div className="p-10 text-slate-500">Loading…</div>;

  const displayBoards = filter ? filterBoards(data.boards, filter) : data.boards;
  const renderBoard = displayBoards.find((b) => b.board_id === boardId) || displayBoards[0];
  const activeLabel = SUMMARY.find((s) => s.key === filter)?.label;
  const toggleFilter = (key) => {
    const next = filter === key ? null : key;
    setFilter(next);
    if (next) {
      const fb = filterBoards(data.boards, next);
      if (!fb.some((b) => b.board_id === boardId)) setBoardId(fb[0]?.board_id ?? null);
      requestAnimationFrame(() => boardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8 space-y-6" data-testid="my-day-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="overline">Personal</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Sun className="h-7 w-7 text-amber-500" />My Day</h1>
          <p className="text-sm text-slate-500 mt-1">Your private to-do boards. Separate from project tasks and workflow. Due dates belong to individual to-dos only.</p>
        </div>
        <div className="flex gap-3" data-testid="my-day-summary">
          {SUMMARY.map((s) => (
            <button key={s.key} onClick={() => toggleFilter(s.key)} aria-pressed={filter === s.key} className={`rounded-lg border px-3 py-2 min-w-[84px] text-left transition ${filter === s.key ? "border-[#2563EB] ring-1 ring-[#2563EB] bg-blue-50/60" : "border-slate-200 bg-white hover:border-slate-400"}`} data-testid={`my-day-count-${s.key}`}>
              <div className={`text-2xl font-bold font-mono ${s.tone}`}>{data.summary[s.key]}</div>
              <div className="overline">{s.label}</div>
            </button>
          ))}
        </div>
      </div>

      {filter && (
        <div className="flex items-center gap-3" data-testid="my-day-filter-bar">
          <span className="text-sm text-slate-500">Showing only</span>
          <button onClick={() => setFilter(null)} className="inline-flex items-center gap-1.5 rounded-full bg-[#2563EB] text-white px-3 py-1 text-sm font-medium" data-testid="my-day-filter-chip">{activeLabel}<X className="h-3.5 w-3.5" /></button>
          <button onClick={() => setFilter(null)} className="text-sm text-slate-500 hover:text-slate-800 underline" data-testid="my-day-clear-filter">Clear filter</button>
        </div>
      )}

      {!filter && (
        <QuickCapture
          items={data.capture || []}
          boards={data.boards}
          onAdd={(title) => run(() => api.post("/todo/capture", { title }))}
          onToggle={(item, completed) => run(() => api.patch(`/todo/items/${item.todo_id}`, { completed }))}
          onEdit={(item) => setEditing(item)}
          onDelete={(item) => run(() => api.delete(`/todo/items/${item.todo_id}`), "Deleted")}
          onMove={(item, checklistId) => run(() => api.patch(`/todo/items/${item.todo_id}`, { checklist_id: checklistId }), "Moved to checklist")}
        />
      )}

      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-3" data-testid="board-tabs">
        {displayBoards.map((b) => (
          <button key={b.board_id} onClick={() => setBoardId(b.board_id)} className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${b.board_id === renderBoard?.board_id ? "bg-[#2563EB] text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"}`} data-testid={`board-tab-${b.board_id}`}>{b.title}</button>
        ))}
        {!filter && (
          <form onSubmit={addBoard} className="flex items-center gap-1.5 ml-auto">
            <Input className="h-8 w-48 text-sm" placeholder="New board…" value={newBoard} onChange={(e) => setNewBoard(e.target.value)} data-testid="board-add-input" />
            <Button type="submit" size="sm" variant="outline" disabled={!newBoard.trim()} data-testid="board-add-btn"><Plus className="h-4 w-4" /></Button>
          </form>
        )}
      </div>

      {!renderBoard ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500" data-testid="my-day-empty">
          {filter ? `No open to-dos match “${activeLabel}”.` : "Create your first board (e.g. “CONTRACTS & NDA”), then add checklists and to-dos."}
        </div>
      ) : (
        <div ref={boardRef}>
          <div className="flex items-center gap-2 mb-3">
            {!filter && renaming ? <Input autoFocus className="h-8 w-72 text-sm font-semibold" value={boardTitle} onChange={(e) => setBoardTitle(e.target.value)} onBlur={saveBoardTitle} onKeyDown={(e) => { if (e.key === "Enter") saveBoardTitle(); if (e.key === "Escape") setRenaming(false); }} data-testid="board-title-input" />
              : <h2 className="text-lg font-semibold text-slate-900" data-testid="board-title">{renderBoard.title}</h2>}
            {!filter && <button className="text-slate-400 hover:text-slate-700" onClick={() => { setBoardTitle(renderBoard.title); setRenaming(true); }} data-testid="board-rename-btn"><Pencil className="h-3.5 w-3.5" /></button>}
            {!filter && <button className="text-slate-400 hover:text-red-600" onClick={deleteBoard} data-testid="board-delete-btn"><Trash2 className="h-3.5 w-3.5" /></button>}
          </div>
          <BoardView board={renderBoard} actions={actions} readOnly={!!filter} />
        </div>
      )}

      {editing && <TodoEditor item={editing} onClose={() => setEditing(null)}
        onSave={(patch) => run(() => api.patch(`/todo/items/${editing.todo_id}`, patch), "To-do updated").then(() => setEditing(null))}
        onDelete={() => run(() => api.delete(`/todo/items/${editing.todo_id}`), "To-do deleted").then(() => setEditing(null))} />}

      <AlertDialog open={!!delChecklist} onOpenChange={(o) => !o && setDelChecklist(null)}>
        <AlertDialogContent data-testid="checklist-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this checklist?</AlertDialogTitle>
            <AlertDialogDescription data-testid="checklist-delete-desc">
              {delChecklist && (() => {
                const td = delChecklist.items?.length || 0;
                const nt = (delChecklist.notes || []).length;
                const parts = [];
                if (td) parts.push(`${td} to-do${td > 1 ? "s" : ""}`);
                if (nt) parts.push(`${nt} note${nt > 1 ? "s" : ""}`);
                const summary = parts.length ? `contains ${parts.join(" and ")}` : "is empty";
                return <>“{delChecklist.title}” {summary}. This permanently deletes the checklist and everything in it. This cannot be undone.</>;
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="checklist-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              data-testid="checklist-delete-confirm"
              onClick={() => { const c = delChecklist; setDelChecklist(null); run(() => api.delete(`/todo/checklists/${c.checklist_id}`), "Checklist deleted"); }}
            >Delete checklist</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
